// CookChow API client (Phases 5 + 6).
//
// Dev workflow: `pnpm run dev` runs the Vite dev server only (faster
// boot for design work). To exercise the API end-to-end run
// `pnpm run dev:netlify` from the repo root — that starts Vite + the
// functions together at http://localhost:8888 and routes `/api/*` to
// the Netlify Functions in `apps/functions/src/` via the redirects in
// `netlify.toml`. The client always posts to `/api/*` regardless.
//
// This module is the only place in the web app permitted to call
// `fetch`. Every endpoint is wrapped here with the wire types from
// `@cookchow/shared`; the rest of the app sees typed promises only.
// Phase 6 adds the three cooking-phase endpoints below.

import type {
  AnchoredPlan,
  AnchorRequest,
  AnchorResponseSuccess,
  AssistEntry,
  AssistIndex,
  CompleteTaskRequest,
  SessionResumeRequest,
  TickRequest,
  TickResponse,
  ValidateRecipeRequest,
  ValidateRecipeResult,
} from '@cookchow/shared';
import { ApiError, fromBadResponse, networkError } from './errors';

const BASE_URL = '/api';

async function postJson<TRes>(path: string, body: unknown): Promise<TRes> {
  let response: Response;
  try {
    response = await fetch(`${BASE_URL}${path}`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    });
  } catch {
    throw new ApiError(networkError());
  }
  if (!response.ok) {
    throw new ApiError(await fromBadResponse(response));
  }
  return (await response.json()) as TRes;
}

async function getJson<TRes>(path: string): Promise<TRes> {
  let response: Response;
  try {
    response = await fetch(`${BASE_URL}${path}`, { method: 'GET' });
  } catch {
    throw new ApiError(networkError());
  }
  if (!response.ok) {
    throw new ApiError(await fromBadResponse(response));
  }
  return (await response.json()) as TRes;
}

export async function validateRecipe(req: ValidateRecipeRequest): Promise<ValidateRecipeResult> {
  return postJson<ValidateRecipeResult>('/validate-recipe', req);
}

export async function anchor(req: AnchorRequest): Promise<AnchoredPlan & { planToken: string }> {
  return postJson<AnchorResponseSuccess>('/anchor', req);
}

// Phase 6 — cooking-phase endpoints. All three return the same shape so
// the tick controller can treat them uniformly.

export async function tick(req: TickRequest): Promise<TickResponse> {
  return postJson<TickResponse>('/tick', req);
}

export async function completeTask(req: CompleteTaskRequest): Promise<TickResponse> {
  return postJson<TickResponse>('/complete-task', req);
}

export async function resumeSession(req: SessionResumeRequest): Promise<TickResponse> {
  return postJson<TickResponse>('/session/resume', req);
}

// Kitchen Assist — the read-only help overlay (README §12.2). Both calls go
// through the app's `/api/assist` proxy; the browser never touches
// cookpit.net directly.

export async function fetchAssistIndex(): Promise<string[]> {
  const res = await getJson<AssistIndex>('/assist');
  return res.words;
}

export async function fetchAssistEntry(word: string): Promise<AssistEntry> {
  const res = await getJson<Partial<AssistEntry>>(`/assist/${encodeURIComponent(word)}`);
  // Guard against a misrouted/old proxy answering with the wrong shape (e.g.
  // the index payload) — a value-less entry must fail, not render a blank
  // help panel.
  if (typeof res.key !== 'string' || typeof res.value !== 'string') {
    throw new ApiError({
      code: 'ASSIST_MALFORMED',
      message: 'The help entry could not be read.',
      recovery: 'Try again in a moment.',
    });
  }
  return { key: res.key, value: res.value };
}
